import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
    User, 
    signInWithEmailAndPassword, 
    signOut as firebaseSignOut,
    onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../data/firebase/FirebaseConfig';

interface AdminAuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    isAuthenticated: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (context === undefined) {
        throw new Error('useAdminAuth must be used within an AdminAuthProvider');
    }
    return context;
};

interface AdminAuthProviderProps {
    children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
            
            // Store authentication state in localStorage for session persistence
            if (currentUser) {
                localStorage.setItem('adminAuth', 'true');
                localStorage.setItem('adminUserId', currentUser.uid);
            } else {
                localStorage.removeItem('adminAuth');
                localStorage.removeItem('adminUserId');
            }
        });

        return () => unsubscribe();
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // onAuthStateChanged will update the user state
        } catch (error: any) {
            throw new Error(error.message || 'Login failed');
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await firebaseSignOut(auth);
            // onAuthStateChanged will update the user state
            localStorage.removeItem('adminAuth');
            localStorage.removeItem('adminUserId');
        } catch (error: any) {
            throw new Error(error.message || 'Logout failed');
        }
    };

    const value: AdminAuthContextType = {
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user
    };

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    );
};

